import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { api } from '@/utils/api';
import { Ticket, User, Settings } from 'lucide-react';

const HomePage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    first_name: '',
    order_id: '',
    email: '',
    phone: '',
    issue_type: '',
    description: '',
  });
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState([]);
  const [issueTypes, setIssueTypes] = useState([]);
  const [selectedIssueTypeConfig, setSelectedIssueTypeConfig] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchIssueTypes();
  }, []);

  const fetchIssueTypes = async () => {
    try {
      const response = await api.get('/issue-types');
      setIssueTypes(response.data);
    } catch (error) {
      console.error('Failed to fetch issue types:', error);
    }
  };

  const handleIssueTypeChange = (value) => {
    setFormData({ ...formData, issue_type: value });
    const selectedType = issueTypes.find(type => type.name === value);
    setSelectedIssueTypeConfig(selectedType?.attachment_config || null);
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    const maxSizeMB = selectedIssueTypeConfig?.max_size_mb || 200;
    const maxSizeBytes = maxSizeMB * 1024 * 1024;

    for (const file of files) {
      if (file.size > maxSizeBytes) {
        toast.error(`File "${file.name}" exceeds ${maxSizeMB}MB limit`);
        continue;
      }

      const uploadId = Date.now() + Math.random();
      setUploadingFiles(prev => [...prev, { id: uploadId, name: file.name, progress: 0 }]);

      try {
        const formData = new FormData();
        formData.append('file', file);

        const response = await api.post('/upload-file', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 300000,
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadingFiles(prev => 
              prev.map(f => f.id === uploadId ? { ...f, progress: percentCompleted } : f)
            );
          }
        });

        setUploadedFiles(prev => [...prev, response.data]);
        setUploadingFiles(prev => prev.filter(f => f.id !== uploadId));
        toast.success(`"${file.name}" uploaded successfully`);
      } catch (error) {
        setUploadingFiles(prev => prev.filter(f => f.id !== uploadId));
        toast.error(`Failed to upload "${file.name}"`);
      }
    }

    e.target.value = '';
  };

  const handleRemoveFile = async (fileId) => {
    try {
      await api.delete(`/upload-file/${fileId}`);
      setUploadedFiles(prev => prev.filter(f => f.file_id !== fileId));
      toast.success('File removed');
    } catch (error) {
      toast.error('Failed to remove file');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (selectedIssueTypeConfig?.required && uploadedFiles.length === 0) {
      toast.error('Please upload the required attachment(s)');
      return;
    }

    setLoading(true);

    try {
      const formDataToSend = new FormData();
      Object.keys(formData).forEach(key => {
        formDataToSend.append(key, formData[key]);
      });
      
      const fileIds = uploadedFiles.map(f => f.file_id).join(',');
      formDataToSend.append('file_ids', fileIds);

      await api.post('/tickets', formDataToSend, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('Ticket submitted successfully! Check your email for confirmation.');
      setFormData({
        first_name: '',
        order_id: '',
        email: '',
        phone: '',
        issue_type: '',
        description: '',
      });
      setUploadedFiles([]);
      setSelectedIssueTypeConfig(null);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to submit ticket. Please try again.';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <nav className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 sm:px-6 py-3 sm:py-4 flex justify-between items-center">
          <div className="flex items-center gap-2 min-w-0">
            <Ticket className="h-5 w-5 text-accent flex-shrink-0" />
            <h1 className="text-base sm:text-xl font-heading font-bold truncate">Desko Customer Support</h1>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <Button variant="ghost" size="sm" onClick={() => navigate('/customer/tracking')} data-testid="customer-tracking-btn" className="h-11">
              <User className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Track Tickets</span>
            </Button>
            <Button variant="ghost" size="sm" onClick={() => navigate('/admin/login')} data-testid="admin-login-btn" className="h-11">
              <Settings className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Admin</span>
            </Button>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          <div className="hidden lg:block lg:col-span-5 space-y-8">
            <div>
              <h2 className="text-4xl md:text-5xl font-heading font-bold tracking-tight mb-4">
                Customer Support Request
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed">
                Please fill out this form to get assistance with your issue. We'll get back to you as soon as possible.
              </p>
            </div>
            <div className="space-y-6 text-muted-foreground">
              <div className="flex gap-4">
                <div className="h-12 w-12 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
                  <Ticket className="h-6 w-6 text-accent" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground mb-1">Quick Response</h3>
                  <p className="text-sm">We aim to respond to all tickets within 48 hours</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="h-12 w-12 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
                  <User className="h-6 w-6 text-accent" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground mb-1">Track Your Tickets</h3>
                  <p className="text-sm">Login to view the status of your submitted tickets</p>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7 col-span-1">
            <Card className="border-border shadow-lg">
              <CardHeader>
                <CardTitle className="text-2xl font-heading">Submit Your Request</CardTitle>
                <CardDescription>Fill in the details below</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="first_name">First Name *</Label>
                      <Input
                        id="first_name"
                        data-testid="first-name-input"
                        value={formData.first_name}
                        onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                        required
                        className="mt-1.5 text-base"
                      />
                    </div>
                    <div>
                      <Label htmlFor="order_id">Order ID *</Label>
                      <Input
                        id="order_id"
                        data-testid="order-id-input"
                        placeholder="1198"
                        value={formData.order_id}
                        onChange={(e) => setFormData({ ...formData, order_id: e.target.value })}
                        required
                        className="mt-1.5 text-base"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      data-testid="email-input"
                      placeholder="example@example.com"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      className="mt-1.5 text-base"
                    />
                  </div>

                  <div>
                    <Label htmlFor="phone">Phone</Label>
                    <Input
                      id="phone"
                      type="tel"
                      data-testid="phone-input"
                      placeholder="+91-XXXXXXXXXX"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      required
                      className="mt-1.5 text-base"
                    />
                  </div>

                  <div>
                    <Label htmlFor="issue_type">Issue Type *</Label>
                    <Select value={formData.issue_type} onValueChange={handleIssueTypeChange} required>
                      <SelectTrigger className="mt-1.5" data-testid="issue-type-select">
                        <SelectValue placeholder="Select the type of issue" />
                      </SelectTrigger>
                      <SelectContent>
                        {issueTypes.map((type) => (
                          <SelectItem key={type.issue_type_id} value={type.name}>
                            {type.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="description">Detailed Description *</Label>
                    <Textarea
                      id="description"
                      data-testid="description-textarea"
                      placeholder="Describe your issue in detail"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      required
                      rows={5}
                      className="mt-1.5"
                    />
                  </div>

                  {selectedIssueTypeConfig?.visible && (
                    <div>
                      <Label htmlFor="attachments">
                        Videos/Attachments {selectedIssueTypeConfig.required ? '*' : '(optional)'}
                      </Label>
                      {selectedIssueTypeConfig.instruction && (
                        <p className="text-sm text-muted-foreground mt-1 mb-2">
                          {selectedIssueTypeConfig.instruction}
                        </p>
                      )}
                      <Input
                        id="attachments"
                        type="file"
                        data-testid="attachments-input"
                        onChange={handleFileSelect}
                        accept={selectedIssueTypeConfig.accept_types}
                        multiple
                        className="mt-1.5 min-h-[44px] text-base"
                        disabled={uploadingFiles.length > 0}
                      />
                      <p className="text-xs text-muted-foreground mt-2">
                        Maximum file size: {selectedIssueTypeConfig.max_size_mb}MB per file. Upload files one by one or select multiple.
                      </p>

                      {uploadingFiles.length > 0 && (
                        <div className="mt-4 space-y-3">
                          <p className="text-sm font-semibold">Uploading...</p>
                          {uploadingFiles.map((file) => (
                            <div key={file.id} className="bg-blue-50 border border-blue-200 p-3 rounded-lg">
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-sm font-medium truncate flex-1">{file.name}</span>
                                <span className="text-sm text-blue-600 ml-2">{file.progress}%</span>
                              </div>
                              <div className="w-full bg-blue-200 rounded-full h-2">
                                <div 
                                  className="bg-blue-600 h-2 rounded-full transition-all duration-300" 
                                  style={{ width: `${file.progress}%` }}
                                />
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {uploadedFiles.length > 0 && (
                        <div className="mt-4 space-y-3">
                          <p className="text-sm font-semibold text-green-700">Uploaded Files ({uploadedFiles.length}):</p>
                          {uploadedFiles.map((file) => (
                            <div key={file.file_id} className="flex items-center justify-between bg-green-50 border border-green-200 p-3 rounded-lg">
                              <div className="flex-1 min-w-0">
                                <span className="text-sm font-medium truncate block">{file.filename}</span>
                                <span className="text-xs text-muted-foreground">{file.size_mb} MB</span>
                              </div>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRemoveFile(file.file_id)}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50 ml-2"
                                data-testid={`remove-file-${file.file_id}`}
                              >
                                Remove
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {!selectedIssueTypeConfig?.visible && (
                    <div>
                      <Label htmlFor="attachments-optional">Attachments (optional)</Label>
                      <Input
                        id="attachments-optional"
                        type="file"
                        data-testid="attachments-input-optional"
                        onChange={handleFileSelect}
                        accept=".pdf,.doc,.docx,.txt,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.webp,video/*"
                        multiple
                        className="mt-1.5 min-h-[44px] text-base"
                        disabled={uploadingFiles.length > 0}
                      />

                      {uploadingFiles.length > 0 && (
                        <div className="mt-4 space-y-3">
                          <p className="text-sm font-semibold">Uploading...</p>
                          {uploadingFiles.map((file) => (
                            <div key={file.id} className="bg-blue-50 border border-blue-200 p-3 rounded-lg">
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-sm font-medium truncate flex-1">{file.name}</span>
                                <span className="text-sm text-blue-600 ml-2">{file.progress}%</span>
                              </div>
                              <div className="w-full bg-blue-200 rounded-full h-2">
                                <div 
                                  className="bg-blue-600 h-2 rounded-full transition-all duration-300" 
                                  style={{ width: `${file.progress}%` }}
                                />
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {uploadedFiles.length > 0 && (
                        <div className="mt-4 space-y-3">
                          <p className="text-sm font-semibold text-green-700">Uploaded Files ({uploadedFiles.length}):</p>
                          {uploadedFiles.map((file) => (
                            <div key={file.file_id} className="flex items-center justify-between bg-green-50 border border-green-200 p-3 rounded-lg">
                              <div className="flex-1 min-w-0">
                                <span className="text-sm font-medium truncate block">{file.filename}</span>
                                <span className="text-xs text-muted-foreground">{file.size_mb} MB</span>
                              </div>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRemoveFile(file.file_id)}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50 ml-2"
                              >
                                Remove
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  <Button type="submit" data-testid="submit-ticket-btn" disabled={loading} className="w-full">
                    {loading ? 'Submitting...' : 'Submit Ticket'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomePage;